import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Eye, 
  Users, 
  Mail, 
  Phone, 
  MessageCircle, 
  Image, 
  Heart, 
  BarChart3,
  Globe,
  Lock,
  UserPlus,
  Activity,
  Save,
  X,
  Check,
  AlertCircle
} from 'lucide-react';
import PrivacyService, { PrivacySettings as PrivacySettingsType } from '../../services/privacy';

interface PrivacySettingsModalProps {
  onClose: () => void;
}

const PrivacySettingsModal: React.FC<PrivacySettingsModalProps> = ({ onClose }) => {
  const [settings, setSettings] = useState<PrivacySettingsType>({
    profile_visibility: 'public',
    discoverable_by_email: true,
    discoverable_by_phone: true,
    discoverable_by_username: true,
    allow_friend_requests: true,
    show_online_status: true,
    show_activity_status: true,
    who_can_message: 'everyone',
    who_can_see_images: 'everyone',
    who_can_see_favorites: 'friends',
    who_can_see_stats: 'everyone'
  });

  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const privacySettings = await PrivacyService.getPrivacySettings();
      if (privacySettings) {
        setSettings(privacySettings);
      }
    } catch (error) {
      console.error('Error loading privacy settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const success = await PrivacyService.updatePrivacySettings(settings);
      
      if (phoneNumber.trim()) {
        await PrivacyService.updatePhoneNumber(phoneNumber.trim());
      }

      if (success) {
        setMessage({ type: 'success', text: 'Privacy settings updated successfully!' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: 'Failed to update privacy settings' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred while saving settings' });
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: keyof PrivacySettingsType, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const ToggleSwitch = ({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) => (
    <button
      onClick={onToggle}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        enabled ? 'bg-purple-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  const SelectOption = ({ 
    value, 
    options, 
    onChange, 
    icon: Icon 
  }: { 
    value: string; 
    options: { value: string; label: string }[]; 
    onChange: (value: string) => void;
    icon: React.ElementType;
  }) => (
    <div className="flex items-center space-x-3">
      <Icon className="h-5 w-5 text-gray-500" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  const SettingRow = ({ 
    icon: Icon, 
    title, 
    description, 
    children 
  }: { 
    icon: React.ElementType; 
    title: string; 
    description: string; 
    children: React.ReactNode;
  }) => (
    <div className="flex items-center justify-between p-4 border-b border-gray-200 last:border-b-0">
      <div className="flex items-start space-x-3 flex-1">
        <Icon className="h-5 w-5 text-gray-500 mt-1" />
        <div className="flex-1">
          <h3 className="font-medium text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
      </div>
      <div className="ml-4">
        {children}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <span className="ml-3 text-gray-600">Loading privacy settings...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Shield className="h-6 w-6 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Privacy Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`p-4 ${message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border-b`}>
            <div className="flex items-center space-x-2">
              {message.type === 'success' ? (
                <Check className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              )}
              <span className={`text-sm font-medium ${message.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                {message.text}
              </span>
            </div>
          </div>
        )}

        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="p-6 space-y-8">
            {/* Profile Visibility */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <Eye className="h-5 w-5 mr-2 text-purple-600" />
                Profile Visibility
              </h3>
              <div className="space-y-4">
                <SelectOption
                  value={settings.profile_visibility}
                  options={[
                    { value: 'public', label: 'Public - Anyone can see your profile' },
                    { value: 'friends_only', label: 'Friends Only - Only friends can see your profile' },
                    { value: 'private', label: 'Private - Only you can see your profile' }
                  ]}
                  onChange={(value) => updateSetting('profile_visibility', value)}
                  icon={Globe}
                />
              </div>
            </div>

            {/* Discoverability */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <Users className="h-5 w-5 mr-2 text-purple-600" />
                How People Can Find You
              </h3>
              <div className="space-y-4">
                <SettingRow
                  icon={Mail}
                  title="Discoverable by Email"
                  description="Allow people to find you using your email address"
                >
                  <ToggleSwitch
                    enabled={settings.discoverable_by_email}
                    onToggle={() => updateSetting('discoverable_by_email', !settings.discoverable_by_email)}
                  />
                </SettingRow>

                <SettingRow
                  icon={Phone}
                  title="Discoverable by Phone"
                  description="Allow people to find you using your phone number"
                >
                  <ToggleSwitch
                    enabled={settings.discoverable_by_phone}
                    onToggle={() => updateSetting('discoverable_by_phone', !settings.discoverable_by_phone)}
                  />
                </SettingRow>

                <SettingRow
                  icon={Users}
                  title="Discoverable by Username"
                  description="Allow people to find you using your username"
                >
                  <ToggleSwitch
                    enabled={settings.discoverable_by_username}
                    onToggle={() => updateSetting('discoverable_by_username', !settings.discoverable_by_username)}
                  />
                </SettingRow>

                {/* Phone Number Input */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number (Optional)
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Enter your phone number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Connection Settings */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <UserPlus className="h-5 w-5 mr-2 text-purple-600" />
                Connection Settings
              </h3>
              <div className="space-y-4">
                <SettingRow
                  icon={UserPlus}
                  title="Allow Friend Requests"
                  description="Let people send you friend requests"
                >
                  <ToggleSwitch
                    enabled={settings.allow_friend_requests}
                    onToggle={() => updateSetting('allow_friend_requests', !settings.allow_friend_requests)}
                  />
                </SettingRow>
              </div>
            </div>

            {/* Activity Status */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <Activity className="h-5 w-5 mr-2 text-purple-600" />
                Activity Status
              </h3>
              <div className="space-y-4">
                <SettingRow
                  icon={Activity}
                  title="Show Online Status"
                  description="Let others see when you're online"
                >
                  <ToggleSwitch
                    enabled={settings.show_online_status}
                    onToggle={() => updateSetting('show_online_status', !settings.show_online_status)}
                  />
                </SettingRow>

                <SettingRow
                  icon={Activity}
                  title="Show Activity Status"
                  description="Let others see your last activity time"
                >
                  <ToggleSwitch
                    enabled={settings.show_activity_status}
                    onToggle={() => updateSetting('show_activity_status', !settings.show_activity_status)}
                  />
                </SettingRow>
              </div>
            </div>

            {/* Content Visibility */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <Lock className="h-5 w-5 mr-2 text-purple-600" />
                Content Visibility
              </h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Who can message you</label>
                  <SelectOption
                    value={settings.who_can_message}
                    options={[
                      { value: 'everyone', label: 'Everyone' },
                      { value: 'friends', label: 'Friends Only' },
                      { value: 'nobody', label: 'Nobody' }
                    ]}
                    onChange={(value) => updateSetting('who_can_message', value)}
                    icon={MessageCircle}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Who can see your images</label>
                  <SelectOption
                    value={settings.who_can_see_images}
                    options={[
                      { value: 'everyone', label: 'Everyone' },
                      { value: 'friends', label: 'Friends Only' },
                      { value: 'nobody', label: 'Only Me' }
                    ]}
                    onChange={(value) => updateSetting('who_can_see_images', value)}
                    icon={Image}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Who can see your favorites</label>
                  <SelectOption
                    value={settings.who_can_see_favorites}
                    options={[
                      { value: 'everyone', label: 'Everyone' },
                      { value: 'friends', label: 'Friends Only' },
                      { value: 'nobody', label: 'Only Me' }
                    ]}
                    onChange={(value) => updateSetting('who_can_see_favorites', value)}
                    icon={Heart}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Who can see your stats</label>
                  <SelectOption
                    value={settings.who_can_see_stats}
                    options={[
                      { value: 'everyone', label: 'Everyone' },
                      { value: 'friends', label: 'Friends Only' },
                      { value: 'nobody', label: 'Only Me' }
                    ]}
                    onChange={(value) => updateSetting('who_can_see_stats', value)}
                    icon={BarChart3}
                  />
                </div>
              </div>
            </div>

            {/* Privacy Tips */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
                <Shield className="h-5 w-5 mr-2 text-blue-600" />
                Privacy Tips
              </h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Review your privacy settings regularly</li>
                <li>• Be careful about what information you share publicly</li>
                <li>• Use strong, unique passwords for your account</li>
                <li>• Consider making your profile private if you want more control</li>
                <li>• You can always block users who make you uncomfortable</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacySettingsModal; 